.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-inline-size: 0;
}

.label {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-text);
}

.requiredMark {
  color: var(--color-danger-text);
  font-weight: var(--weight-semibold);
}

.optionalMark {
  color: var(--color-text-subtle);
  font-weight: var(--weight-regular);
  font-size: var(--text-xs);
}

.control {
  position: relative;
  display: flex;
  align-items: center;
  min-block-size: var(--control-height);
  background: var(--color-surface);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  transition:
    border-color var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out);
}

.control:hover {
  border-color: var(--color-text-subtle);
}

.control:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 var(--focus-ring-width) color-mix(in srgb, var(--color-focus) 40%, transparent);
}

.field[data-invalid] .control,
.control[data-invalid] {
  border-color: var(--color-danger);
}

.field[data-disabled] .control {
  background: var(--color-surface-sunken);
  opacity: 0.7;
}

.input {
  flex: 1;
  min-inline-size: 0;
  inline-size: 100%;
  border: 0;
  background: transparent;
  padding-inline: var(--space-3);
  padding-block: var(--space-2);
  font-size: var(--text-md);
  line-height: 1.4;
  outline: none;
}

.input:focus-visible,
.input[data-focus-visible] {
  outline: none;
}

.input::placeholder {
  color: var(--color-text-subtle);
}

textarea.input {
  resize: vertical;
  min-block-size: 5rem;
}

.numeric {
  text-align: end;
  font-variant-numeric: tabular-nums lining-nums;
}

.adornment {
  display: inline-flex;
  align-items: center;
  padding-inline: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  white-space: nowrap;
}

.description {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  line-height: 1.5;
}

.error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--color-danger-text);
  line-height: 1.5;
}

.error svg {
  flex: none;
  margin-block-start: 0.15em;
}

/* Trigger button inside a control (select, combobox, date picker) */
.trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-inline-size: 2.25rem;
  padding: 0;
  border: 0;
  border-inline-start: var(--border-width) solid var(--color-rule);
  background: transparent;
  color: var(--color-text-muted);
  border-start-end-radius: var(--radius-md);
  border-end-end-radius: var(--radius-md);
  cursor: pointer;
}

.trigger[data-hovered] {
  background: var(--color-surface-hover);
  color: var(--color-text);
}

/* Select trigger: the whole control is the button */
.selectButton {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  inline-size: 100%;
  min-block-size: var(--control-height);
  padding-inline: var(--space-3) var(--space-2);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  text-align: start;
  cursor: pointer;
}

.selectButton[data-hovered] {
  border-color: var(--color-text-subtle);
}

.selectButton[data-focus-visible] {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 var(--focus-ring-width) color-mix(in srgb, var(--color-focus) 40%, transparent);
}

.field[data-invalid] .selectButton {
  border-color: var(--color-danger);
}

.selectValue {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selectValue[data-placeholder] {
  color: var(--color-text-subtle);
}

/* Popover + list box */
.popover {
  min-inline-size: var(--trigger-width);
  max-inline-size: min(36rem, calc(100vw - 2 * var(--space-4)));
  max-block-size: min(22rem, 60dvh);
  overflow: auto;
  background: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  outline: none;
}

.popover[data-entering] {
  animation: pop-in var(--duration-base) var(--ease-out);
}

@keyframes pop-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.listBox {
  padding: var(--space-1);
  outline: none;
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding-block: var(--space-2);
  padding-inline: var(--space-3);
  border-radius: var(--radius-sm);
  cursor: pointer;
  outline: none;
  color: var(--color-text);
}

.option[data-focused],
.option[data-hovered] {
  background: var(--color-primary-subtle);
  color: var(--color-primary-subtle-text);
}

.option[data-selected] {
  font-weight: var(--weight-semibold);
}

.option[data-disabled] {
  color: var(--color-text-subtle);
  cursor: not-allowed;
}

.optionCheck {
  margin-inline-start: auto;
  color: var(--color-primary);
}

.optionBody {
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
}

.optionTitle {
  overflow: hidden;
  text-overflow: ellipsis;
}

.optionMeta {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.emptyState {
  padding: var(--space-3);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* Checkbox and radio */
.check {
  display: inline-flex;
  align-items: flex-start;
  gap: var(--space-2);
  cursor: pointer;
  font-size: var(--text-md);
}

.checkBox {
  flex: none;
  display: inline-grid;
  place-items: center;
  inline-size: 1.125rem;
  block-size: 1.125rem;
  margin-block-start: 0.2em;
  border: 1.5px solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-on-primary);
  transition: background-color var(--duration-fast) var(--ease-out);
}

.check[data-selected] .checkBox {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

.check[data-focus-visible] .checkBox {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}

.check[data-disabled] {
  cursor: not-allowed;
  opacity: 0.6;
}

.checkText {
  min-inline-size: 0;
}

.checkGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-inline-size: 0;
}

.checkList {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
}

.radioGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.radioList {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: var(--space-2);
}

.radioCard {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  cursor: pointer;
}

.radioCard[data-selected] {
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
  box-shadow: inset 0 0 0 1px var(--color-primary);
}

.radioCard[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}

.radioDot {
  flex: none;
  inline-size: 1.125rem;
  block-size: 1.125rem;
  margin-block-start: 0.15em;
  border: 1.5px solid var(--color-border-strong);
  border-radius: 50%;
  background: var(--color-surface);
}

.radioCard[data-selected] .radioDot {
  border: 5px solid var(--color-primary);
}

.radioText {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.radioTitle {
  font-weight: var(--weight-semibold);
}

.radioDescription {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

/* Segmented control (two or three exclusive options) */
.segmented {
  display: inline-flex;
  padding: 2px;
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.segment {
  min-block-size: calc(var(--control-height) - 6px);
  padding-inline: var(--space-3);
  border: 0;
  border-radius: calc(var(--radius-md) - 2px);
  background: transparent;
  color: var(--color-text-muted);
  font-weight: var(--weight-medium);
  cursor: pointer;
}

.segment[data-selected] {
  background: var(--color-surface);
  color: var(--color-text);
  box-shadow: var(--shadow-sm);
}

.segment[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 1px;
}

/* Date picker */
.dateInput {
  display: flex;
  flex: 1;
  padding-inline: var(--space-3);
  padding-block: var(--space-2);
  font-variant-numeric: tabular-nums;
  unicode-bidi: isolate;
}

.segmentPart {
  padding-inline: 1px;
  border-radius: 2px;
  outline: none;
}

.segmentPart[data-placeholder] {
  color: var(--color-text-subtle);
}

.segmentPart[data-type='literal'] {
  color: var(--color-text-subtle);
}

.segmentPart[data-focused] {
  background: var(--color-primary);
  color: var(--color-on-primary);
}

.calendar {
  padding: var(--space-3);
}

.calendarHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  margin-block-end: var(--space-2);
}

.calendarHeading {
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
}

.calendarGrid {
  border-collapse: collapse;
}

.calendarGrid th {
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  padding-block-end: var(--space-1);
}

.calendarCell {
  display: grid;
  place-items: center;
  inline-size: 2.25rem;
  block-size: 2.25rem;
  border-radius: var(--radius-sm);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  outline: none;
}

.calendarCell[data-outside-month] {
  display: none;
}

.calendarCell[data-hovered] {
  background: var(--color-surface-hover);
}

.calendarCell[data-selected] {
  background: var(--color-primary);
  color: var(--color-on-primary);
}

.calendarCell[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
}

.calendarCell[data-disabled] {
  color: var(--color-text-subtle);
  cursor: default;
}

.fieldRow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(15rem, 100%), 1fr));
  gap: var(--space-4);
}

.warning {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  padding: var(--space-2) var(--space-3);
  border: var(--border-width) solid var(--color-warning-border);
  border-radius: var(--radius-md);
  background: var(--color-warning-bg);
  color: var(--color-warning-text);
  font-size: var(--text-sm);
}

.warning svg {
  flex: none;
  margin-block-start: 0.2em;
}

/* Choice cards: the path is chosen first (DESIGN_SYSTEM §6, ChoiceCards). */
.choiceCards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(13rem, 100%), 1fr));
  gap: var(--space-2);
}

.choiceCard {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  min-block-size: 2.75rem;
  padding: var(--space-3) var(--space-4);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  text-align: start;
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-out),
    background-color var(--duration-fast) var(--ease-out);
}

.choiceCard[data-hovered] {
  border-color: var(--color-text-subtle);
}

.choiceCard[data-selected] {
  border-color: var(--color-primary);
  background: var(--color-primary-subtle);
  box-shadow: inset 0 0 0 1px var(--color-primary);
}

.choiceCard[data-focus-visible] {
  outline: var(--focus-ring-width) solid var(--color-focus);
  outline-offset: 2px;
}

.choiceCard[data-disabled] {
  cursor: not-allowed;
  opacity: 0.6;
}

.choiceIcon {
  flex: none;
  display: inline-grid;
  place-items: center;
  inline-size: 2rem;
  block-size: 2rem;
  border-radius: var(--radius-md);
  background: var(--color-accent-subtle);
  color: var(--color-accent-text);
}

.choiceCard[data-selected] .choiceIcon {
  background: var(--color-primary);
  color: var(--color-on-primary);
}

@media (prefers-reduced-motion: reduce) {
  .choiceCard {
    transition: none;
  }
}
